import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ClipboardCheck, Plus, Calendar, MapPin, Loader2,
  Trash2, Camera, Ruler, Sparkles, FileText, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisitesChantier() {
  const [visites, setVisites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/visites`)
      .then(r => setVisites(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const createAndNavigate = async () => {
    setCreating(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const time = new Date().toTimeString().slice(0, 5);
      const res = await axios.post(`${API}/visites`, {
        chantier_nom: "Nouvelle visite",
        date_visite: today,
        heure_visite: time,
      });
      navigate(`/visites/${res.data.id}`);
    } catch {
      toast.error("Erreur lors de la création");
      setCreating(false);
    }
  };

  const deleteVisite = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette visite ?")) return;
    try {
      await axios.delete(`${API}/visites/${id}`);
      setVisites(prev => prev.filter(v => v.id !== id));
      toast.success("Visite supprimée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) return (
    <div className="space-y-4 p-4 max-w-3xl mx-auto">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-24 bg-white rounded-2xl border border-[#E2E8F0] animate-pulse" />
      ))}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between pt-2">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F2744]">
            Visites Chantier
          </h1>
          <p className="text-sm text-[#64748B]">
            {visites.length} visite{visites.length !== 1 ? "s" : ""} enregistrée{visites.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={createAndNavigate}
          disabled={creating}
          data-testid="btn-new-visite"
          className="flex items-center gap-2 bg-[#0F2744] hover:bg-[#1A3557] text-white px-4 py-2.5 rounded-xl shadow-md font-semibold text-sm transition-colors disabled:opacity-50"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Nouvelle visite
        </button>
      </div>

      {/* Empty state */}
      {visites.length === 0 ? (
        <div className="text-center py-16 text-[#94A3B8]">
          <div className="w-20 h-20 bg-[#EFF6FF] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardCheck className="w-10 h-10 text-[#3B82F6] opacity-60" />
          </div>
          <p className="font-semibold text-[#475569] text-lg">Aucune visite enregistrée</p>
          <p className="text-sm mt-1 mb-5">Documentez vos visites de chantier</p>
          <button
            onClick={createAndNavigate}
            disabled={creating}
            className="bg-[#F97316] hover:bg-orange-500 text-white px-6 py-3 rounded-xl font-bold text-sm transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Commencer une visite
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {visites.map(v => {
            const blocs = v.blocs || [];
            const nbMesures = blocs.filter(b => b.type === "mesure").length;
            const nbPhotos = blocs.filter(b => b.type === "photo").length;
            const nbBlocs = blocs.length;

            return (
              <div
                key={v.id}
                data-testid={`visite-card-${v.id}`}
                onClick={() => navigate(`/visites/${v.id}`)}
                className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm hover:shadow-md hover:border-[#3B82F6]/40 transition-all cursor-pointer active:scale-[0.99] group"
              >
                <div className="p-4 flex items-center gap-3">
                  {/* Icon */}
                  <div className="w-11 h-11 bg-[#EFF6FF] rounded-xl flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-[#3B82F6]" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#0F2744] text-base leading-tight truncate">
                      {v.chantier_nom}
                    </h3>
                    {v.client && (
                      <p className="text-xs text-[#64748B] truncate">{v.client}</p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-[#94A3B8]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {v.date_visite}{v.heure_visite ? ` à ${v.heure_visite}` : ""}
                      </span>
                      {v.adresse && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{v.adresse}
                        </span>
                      )}
                      {nbMesures > 0 && (
                        <span className="flex items-center gap-1">
                          <Ruler className="w-3 h-3" />{nbMesures} mesure{nbMesures > 1 ? "s" : ""}
                        </span>
                      )}
                      {nbPhotos > 0 && (
                        <span className="flex items-center gap-1">
                          <Camera className="w-3 h-3" />{nbPhotos} photo{nbPhotos > 1 ? "s" : ""}
                        </span>
                      )}
                      {nbBlocs > 0 && nbMesures === 0 && nbPhotos === 0 && (
                        <span>{nbBlocs} bloc{nbBlocs > 1 ? "s" : ""}</span>
                      )}
                    </div>
                    {/* Badges */}
                    <div className="flex gap-2 mt-1.5">
                      {v.analyse_ia && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-600 border border-purple-100 rounded-full text-[10px] font-semibold flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" />Analysé IA
                        </span>
                      )}
                      {v.rapport && (
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[10px] font-semibold flex items-center gap-1">
                          <FileText className="w-2.5 h-2.5" />Rapport
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => deleteVisite(e, v.id)}
                      data-testid={`delete-visite-${v.id}`}
                      className="p-2 hover:bg-red-50 rounded-xl text-[#CBD5E1] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="p-1 text-[#CBD5E1] group-hover:text-[#3B82F6] transition-colors">
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
